// Type-safe accessors that guarantee data availability at call time.
// Use these in code that runs after <AppBootstrap> renders its children (API modules,
// controllers, components). They throw with clear diagnostics if called too early.

import type { GridResources, User, UUID } from '@visualizer/types';
import type { AxiosInstance } from 'axios';
import { getSessionStore } from './SessionStore';
import { getResourceStore } from './ResourceStore';
import { getApiClient } from './ApiClientStore';
import { getAppPhase, AppPhase } from './AppPhaseStore';

export type AuthenticatedSession = {
    user: User;
    token: string;
    organizationId: string;
};

export type LoadedResources = {
    gridId: UUID;
    projectId: string;
    gridResources: GridResources;
};

export type ReadyApp = AuthenticatedSession &
    LoadedResources & {
        apiClient: AxiosInstance;
    };

/**
 * Returns the authenticated session or throws if auth hasn't completed.
 * Safe to call in any code that runs after AppBootstrap (components, actions, API modules).
 */
export function requireSession(): AuthenticatedSession {
    const session = getSessionStore();
    if (session.status !== 'authenticated') {
        throw new Error(
            `[requireSession] Called before authentication completed. Current status: "${session.status}". ` +
                `This function can only be called in code that runs after <AppBootstrap> renders its children.`
        );
    }
    return {
        user: session.user,
        token: session.token,
        organizationId: session.organizationId,
    };
}

/**
 * Returns grid resources or throws if resources haven't loaded.
 * Safe to call in any code that runs after AppBootstrap.
 */
export function requireResources(): LoadedResources {
    const resource = getResourceStore();
    if (resource.status !== 'loaded' || !resource.gridResources) {
        throw new Error(
            `[requireResources] Called before resources loaded. Current status: "${resource.status}". ` +
                `This function can only be called in code that runs after <AppBootstrap> renders its children.`
        );
    }
    return {
        gridId: resource.gridId,
        projectId: resource.projectId,
        gridResources: resource.gridResources,
    };
}

/**
 * Returns the full "ready" app state — session + resources + apiClient.
 * The single point of access for code that needs everything.
 */
export function requireApp(): ReadyApp {
    const phase = getAppPhase();
    if (phase !== AppPhase.READY) {
        throw new Error(
            `[requireApp] Called during phase "${phase}". ` +
                `This function can only be called in code that runs after <AppBootstrap> renders its children (phase = "ready").`
        );
    }
    const session = requireSession();
    const resources = requireResources();
    return {
        ...session,
        ...resources,
        apiClient: getApiClient(),
    };
}

/**
 * Returns grid resources if available, or null during early phases.
 * Use this in code that might run before/after bootstrap (e.g., hydration callbacks).
 */
export function getGridResourcesIfReady(): GridResources | null {
    const resource = getResourceStore();
    return resource.status === 'loaded' ? resource.gridResources : null;
}
